/**
 * Words the sale screen shows for an item — shop words only (no system terms, design/04 §0):
 * the name in the staff member's language, why a card cannot be sold ("10:02にeBayで販売済み"),
 * the grade label ("PSA 10"). Nothing here touches money.
 */
import type { TFunction } from "i18next";

export interface LocalizedText {
  ja?: string | null;
  en?: string | null;
}

/** ja staff read Japanese names; th / en staff read the English name when there is one. */
export function localized(text: LocalizedText | null | undefined, locale: string): string {
  if (!text) {
    return "";
  }
  if (locale === "ja") {
    return text.ja ?? text.en ?? "";
  }
  return text.en ?? text.ja ?? "";
}

export interface UnavailableInfo {
  reason?: string | null;
  soldAt?: string | null;
  soldVia?: string | null;
  holdInfo?: { ownerLabel?: string | null } | null;
  holdBy?: { ownerLabel?: string | null } | null;
}

const KNOWN_VIA = new Set(["pos", "web", "ebay", "whatnot"]);

function when(iso: string, locale: string, now = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return "";
  }
  const sameDay = at.toDateString() === now.toDateString();
  return new Intl.DateTimeFormat(locale === "ja" ? "ja-JP" : locale === "th" ? "th-TH" : "en-GB", {
    timeZone: "Asia/Tokyo",
    ...(sameDay
      ? { hour: "2-digit", minute: "2-digit" }
      : { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }),
  }).format(at);
}

/** 「この商品は10:02にeBayで販売済みです」 / 「保留中（オンラインのお会計）」 / reason words. */
export function unavailableText(info: UnavailableInfo, t: TFunction, locale: string): string {
  if (info.soldAt) {
    const via = info.soldVia && KNOWN_VIA.has(info.soldVia) ? info.soldVia : "other";
    return t("tile.sold_at", { when: when(info.soldAt, locale), via: t(`via.${via}`) });
  }
  const hold = info.holdInfo ?? info.holdBy;
  if (hold?.ownerLabel) {
    return t("tile.held_by", { who: hold.ownerLabel });
  }
  if (info.reason) {
    return t(`reason.${info.reason}`, { defaultValue: t("tile.unavailable") });
  }
  return t("tile.unavailable");
}

export interface GradingInfo {
  company?: string | null;
  grade?: string | null;
  certNumber?: string | null;
}

/** "PSA 10" */
export function gradeLabel(grading: GradingInfo | null | undefined): string | null {
  if (!grading?.company && !grading?.grade) {
    return null;
  }
  return [grading.company, grading.grade].filter(Boolean).join(" ");
}

export function isSealed(productType: string | null | undefined): boolean {
  return productType === "sealed";
}

/**
 * hh:mm (Japan time) of a live change — 「10:02に売れました」 (T07-40); the date too when it was not
 * today.
 */
export function clockTime(iso: string, locale: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return "";
  }
  const tag = locale === "ja" ? "ja-JP" : locale === "th" ? "th-TH" : "en-GB";
  const day = (d: Date) =>
    new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", dateStyle: "short" }).format(d);
  const today = day(at) === day(now);
  return new Intl.DateTimeFormat(tag, {
    timeZone: "Asia/Tokyo",
    ...(today ? {} : { month: "numeric", day: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
  }).format(at);
}
